import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, money } from "../api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import AddressField from "../components/AddressField";
import MapView from "../components/MapView";
import InfoTip from "../components/InfoTip";
import { Plus, X, Loader2, MapPin, Truck, Clock, Package, CheckCircle2, Bookmark, ArrowLeft, ArrowRight } from "lucide-react";

const inputCls = "cr-input";
const STEPS = [
  ["stops", "Stops", MapPin],
  ["vehicle", "Vehicle", Truck],
  ["timing", "Timing", Clock],
  ["orders", "Orders", Package],
  ["review", "Review", CheckCircle2],
];

export default function BookPage({ embedded = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [pickup, setPickup] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [stops, setStops] = useState([]);
  const [cargo, setCargo] = useState({ description: "", weight_kg: "", pallets: "", quantity: "" });
  const [vehicle, setVehicle] = useState("van_small");
  const [service, setService] = useState("standard");
  const [pricingMode, setPricingMode] = useState("bidding");
  const [dates, setDates] = useState({ pickup_date: "", delivery_date: "" });
  const [notes, setNotes] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [addresses, setAddresses] = useState([]);

  useEffect(() => {
    if (user?.role === "company") api.get("/company/addresses").then((r) => setAddresses(r.data.addresses)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!pickup?.lat || !delivery?.lat) { setQuote(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/quotes", {
          pickup: { lat: pickup.lat, lng: pickup.lng },
          delivery: { lat: delivery.lat, lng: delivery.lng },
          vehicle_type: vehicle, service_level: service,
        });
        setQuote(data);
      } catch { setQuote(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [pickup, delivery, vehicle, service]);

  if (!user || (user.role !== "company" && user.role !== "admin")) {
    return <div className="max-w-md mx-auto px-4 py-20 text-center"><Seo title="Book a courier | CourierRadar" noindex /><p className="text-sm text-zinc-600">Sign in with a company account to book deliveries.</p></div>;
  }

  const applyAddress = (id, setter) => {
    const a = addresses.find((x) => x.id === id);
    if (a) setter({ address: a.address, city: a.city, postcode: a.postcode, country: a.country, lat: a.lat, lng: a.lng });
  };

  const saveAddress = async (point, kind) => {
    if (!point?.address) return toast.error("Select an address first");
    const label = window.prompt("Label for this address (e.g. Main warehouse):", point.city || kind);
    if (!label) return;
    try {
      await api.post("/company/addresses", { label, address: point.address, city: point.city, postcode: point.postcode, country: point.country, lat: point.lat, lng: point.lng });
      toast.success("Saved to address book");
      const { data } = await api.get("/company/addresses");
      setAddresses(data.addresses);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const canNext = () => {
    if (step === 0) return !!(pickup?.lat && delivery?.lat);
    return true;
  };

  const next = () => {
    if (!canNext()) return setError("Select pickup and delivery addresses from the suggestions");
    setError("");
    setStep(Math.min(step + 1, STEPS.length - 1));
  };

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data: job } = await api.post("/jobs", {
        pickup, delivery, stops,
        cargo: { ...cargo, weight_kg: cargo.weight_kg ? +cargo.weight_kg : null, pallets: cargo.pallets ? +cargo.pallets : null, quantity: cargo.quantity ? +cargo.quantity : null },
        vehicle_type: vehicle, service_level: service, pricing_mode: pricingMode,
        price_net: pricingMode === "fixed" ? quote?.price_net ?? null : null,
        currency: quote?.currency || "GBP",
        pickup_date: dates.pickup_date || null, delivery_date: dates.delivery_date || null,
        notes, is_public: isPublic,
      });
      toast.success(`Job ${job.ref} published`);
      navigate(`/jobs/${job.id}`);
    } catch (err) { setError(fmtErr(err)); setBusy(false); }
  };

  const SavedRow = ({ onPick, testId }) => addresses.length === 0 ? null : (
    <div className="flex items-center gap-2">
      <Bookmark className="w-3.5 h-3.5 text-[#A09D95] shrink-0" />
      <select data-testid={testId} defaultValue="" onChange={(e) => { onPick(e.target.value); e.target.value = ""; }}
        className="cr-input !py-1.5 !text-xs !rounded-full !w-auto">
        <option value="" disabled>Saved addresses…</option>
        {addresses.map((a) => <option key={a.id} value={a.id}>{a.label} — {a.city || a.address.slice(0, 25)}</option>)}
      </select>
    </div>
  );

  return (
    <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 py-8"} data-testid="book-page">
      {!embedded && <Seo title="Book a courier | CourierRadar" noindex />}
      {!embedded && <h1 className="font-heading text-2xl font-semibold tracking-tight mb-6">Book delivery</h1>}

      <div className={embedded ? "grid xl:grid-cols-[1fr_360px] gap-6 items-start" : "grid lg:grid-cols-[190px_1fr_380px] gap-6 items-start"}>
        {/* Steps rail */}
        <nav className={embedded ? "flex gap-1 overflow-x-auto xl:col-span-2" : "flex lg:flex-col gap-1 overflow-x-auto lg:sticky lg:top-20"} data-testid="wizard-steps">
          {STEPS.map(([id, label, Icon], i) => (
            <button key={id} type="button" data-testid={`wizard-step-${id}`} onClick={() => i < step && setStep(i)}
              className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                i === step ? "bg-[#1C1B1A] text-white" : i < step ? "text-[#1C1B1A] hover:bg-[#F6F3EA]" : "text-[#A09D95] cursor-default"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
              {i < step && <CheckCircle2 className="w-3 h-3 text-[#F2D06B] lg:ml-auto" />}
            </button>
          ))}
        </nav>

        {/* Step content */}
        <div className="cr-card !p-6 min-h-[420px]" data-testid="wizard-content">
          {step === 0 && (
            <div className="space-y-4">
              <h2 className="text-base font-bold">Where are we going?</h2>
              <SavedRow testId="addr-pick-pickup" onPick={(id) => applyAddress(id, setPickup)} />
              <div className="flex gap-2 items-start">
                <div className="flex-1"><AddressField label="Pickup" testId="book-pickup" value={pickup} onSelect={setPickup} /></div>
                <button type="button" data-testid="addr-save-pickup" onClick={() => saveAddress(pickup, "Pickup")}
                  className="mt-8 p-2 text-[#A09D95] hover:text-[#1C1B1A]" title="Save to address book"><Bookmark className="w-4 h-4" /></button>
              </div>
              {stops.map((s, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <AddressField label={`Stop ${i + 1}`} testId={`book-stop-${i}`} value={s}
                      onSelect={(v) => setStops(stops.map((x, xi) => (xi === i ? v : x)))} />
                  </div>
                  <button type="button" data-testid={`remove-stop-${i}`} onClick={() => setStops(stops.filter((_, xi) => xi !== i))}
                    className="mt-8 p-2 text-zinc-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <button type="button" data-testid="add-stop-btn" onClick={() => setStops([...stops, null])}
                className="text-xs font-semibold text-[#1C1B1A] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add stop</button>
              <div className="flex gap-2 items-start">
                <div className="flex-1"><AddressField label="Delivery" testId="book-delivery" value={delivery} onSelect={setDelivery} /></div>
                <button type="button" data-testid="addr-save-delivery" onClick={() => saveAddress(delivery, "Delivery")}
                  className="mt-8 p-2 text-[#A09D95] hover:text-[#1C1B1A]" title="Save to address book"><Bookmark className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-base font-bold">What vehicle do you need?</h2>
              <div className="grid grid-cols-2 gap-2.5">
                {[["bike", "Cargo bike"], ["car", "Car"], ["van_small", "Small van"], ["van_large", "Large van"], ["box_truck", "Box truck"], ["semi_trailer", "Semi-trailer"]].map(([v, l]) => (
                  <button type="button" key={v} data-testid={`vehicle-${v}`} onClick={() => setVehicle(v)}
                    className={`border rounded-[20px] p-4 text-left transition-all ${vehicle === v ? "border-[#1C1B1A] ring-2 ring-[#1C1B1A]/15 bg-[#FCFAF5]" : "border-[#E8E4D9] hover:border-[#1C1B1A]/30"}`}>
                    <Truck className="w-5 h-5 mb-2 text-[#1C1B1A]" />
                    <p className="text-sm font-bold">{l}</p>
                  </button>
                ))}
              </div>
              <div>
                <label className="cr-label mb-1.5 block">Service level
                  <InfoTip text="Standard = scheduled. Same-day = delivered today. Hotshot = direct drive now. Dedicated = vehicle only for you." /></label>
                <select data-testid="book-service" value={service} onChange={(e) => setService(e.target.value)} className={inputCls}>
                  <option value="standard">Standard</option><option value="same_day">Same-day</option>
                  <option value="hotshot">Hotshot</option><option value="dedicated">Dedicated</option>
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-base font-bold">When?</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="cr-label mb-1.5 block">Pickup date</label>
                  <input type="date" data-testid="pickup-date" value={dates.pickup_date} onChange={(e) => setDates({ ...dates, pickup_date: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="cr-label mb-1.5 block">Delivery date</label>
                  <input type="date" data-testid="delivery-date" value={dates.delivery_date} onChange={(e) => setDates({ ...dates, delivery_date: e.target.value })} className={inputCls} />
                </div>
              </div>
              <p className="text-xs text-[#A09D95]">Leave blank for flexible timing — couriers will propose dates in their bids.</p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-base font-bold">What are we moving?</h2>
              <input data-testid="cargo-description" placeholder="What are you shipping? (e.g. 2 EUR-pallets of parts)" value={cargo.description}
                onChange={(e) => setCargo({ ...cargo, description: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="cr-label mb-1.5 block">Weight (kg)</label>
                  <input data-testid="cargo-weight" type="number" min="0" value={cargo.weight_kg} onChange={(e) => setCargo({ ...cargo, weight_kg: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="cr-label mb-1.5 block">Pallets</label>
                  <input data-testid="cargo-pallets" type="number" min="0" value={cargo.pallets} onChange={(e) => setCargo({ ...cargo, pallets: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="cr-label mb-1.5 block">Items</label>
                  <input data-testid="cargo-quantity" type="number" min="0" value={cargo.quantity} onChange={(e) => setCargo({ ...cargo, quantity: e.target.value })} className={inputCls} />
                </div>
              </div>
              <textarea data-testid="book-notes" placeholder="Notes for the courier (access codes, loading bay, CMR details…)" value={notes}
                onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} />
              <label className="flex items-center gap-2 text-sm text-zinc-600">
                <input type="checkbox" data-testid="public-checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="accent-[#1C1B1A]" />
                List publicly on the load board
                <InfoTip text="Public jobs appear on the indexable load board and in search engines. Exact addresses stay private until assignment." />
              </label>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="wizard-review">
              <h2 className="text-base font-bold">Review & publish</h2>
              <div className="bg-[#F6F3EA] rounded-[20px] p-4 text-xs space-y-1.5">
                <p><span className="text-[#A09D95]">Route:</span> <b>{pickup?.city || pickup?.address} → {delivery?.city || delivery?.address}</b>{stops.filter(Boolean).length > 0 && ` (+${stops.filter(Boolean).length} stops)`}</p>
                <p><span className="text-[#A09D95]">Vehicle:</span> <b className="capitalize">{vehicle.replace(/_/g, " ")}</b> · {service.replace(/_/g, " ")}</p>
                <p><span className="text-[#A09D95]">Timing:</span> <b>{dates.pickup_date || "Flexible"}{dates.delivery_date ? ` → ${dates.delivery_date}` : ""}</b></p>
                {cargo.description && <p><span className="text-[#A09D95]">Cargo:</span> <b>{cargo.description}</b></p>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[{ id: "bidding", l: "Open for bids", tip: "Couriers bid; you compare and pick." },
                  { id: "fixed", l: "Fixed price", tip: "Instant accept at the shown price." }].map((m) => (
                  <button type="button" key={m.id} data-testid={`pricing-${m.id}`} onClick={() => setPricingMode(m.id)}
                    className={`border rounded-[20px] p-3.5 text-left transition-all ${pricingMode === m.id ? "border-[#1C1B1A] ring-2 ring-[#1C1B1A]/15" : "border-[#E8E4D9]"}`}>
                    <p className="text-sm font-bold">{m.l}</p>
                    <p className="text-[10px] text-[#78756E] mt-0.5">{m.tip}</p>
                  </button>
                ))}
              </div>
              {error && <p data-testid="book-error" className="text-xs text-red-600">{error}</p>}
            </div>
          )}
        </div>

        {/* Live map */}
        <div className={embedded ? "hidden xl:block sticky top-20" : "hidden lg:block sticky top-20"}>
          <div className="rounded-[28px] overflow-hidden border border-[#E8E4D9]" data-testid="wizard-map">
            <MapView height="420px" route={quote?.geometry || null}
              jobs={[
                ...(pickup?.lat ? [{ id: "p", ref: "A", status: "open", pickup: { lat: pickup.lat, lng: pickup.lng, city: pickup.city } }] : []),
                ...(delivery?.lat ? [{ id: "d", ref: "B", status: "assigned", pickup: { lat: delivery.lat, lng: delivery.lng, city: delivery.city } }] : []),
              ]} />
          </div>
        </div>
      </div>

      {/* Bottom summary bar */}
      <div className="sticky bottom-3 mt-6 bg-[#1C1B1A] text-white rounded-full px-5 py-3 flex items-center justify-between gap-3 shadow-[0_16px_50px_rgba(28,27,26,0.3)]" data-testid="wizard-bar">
        <div className="flex items-center gap-4 sm:gap-6 text-[11px] overflow-x-auto">
          <span>Distance: <b className="text-[#F2D06B]">{quote ? `${quote.distance_km} km` : "—"}</b></span>
          <span className="hidden sm:inline">Vehicle: <b className="text-[#F2D06B] capitalize">{vehicle.replace(/_/g, " ")}</b></span>
          <span className="hidden sm:inline">Timing: <b className="text-[#F2D06B]">{dates.pickup_date || "Flexible"}</b></span>
          <span>Est. total: <b className="text-[#F2D06B]" data-testid="book-quote">{quote ? `${money(quote.price_net ?? quote.price_gross, quote.currency)} ex VAT` : "—"}</b></span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {step > 0 && (
            <button type="button" data-testid="wizard-back" onClick={() => setStep(step - 1)}
              className="flex items-center gap-1 text-xs font-semibold px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" data-testid="wizard-next" onClick={next}
              className="flex items-center gap-1 bg-[#F2D06B] hover:bg-[#EFCB68] text-[#1C1B1A] text-xs font-bold px-5 py-2 rounded-full transition-colors">
              Next <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button type="button" data-testid="book-submit-btn" onClick={submit} disabled={busy}
              className="flex items-center gap-1.5 bg-[#F2D06B] hover:bg-[#EFCB68] text-[#1C1B1A] text-xs font-bold px-5 py-2 rounded-full transition-colors disabled:opacity-40">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Publish job
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
